/**
 * /service-request/new/:serviceType — Intake page for starting a new service request.
 *
 * Shows the IntakeForm (financial year, optional portal password).
 * On submit → creates the SR → navigates to /service-request/:id.
 *
 * serviceType param is kebab-case (e.g. "itr-filing") — converted to snake_case
 * before hitting the API via kebabToSnake from @finmin/utils.
 */
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { kebabToSnake } from '@finmin/utils';
import { useGroupedServices } from '@/api/services';
import { IntakeForm } from '@/features/service-request/components/intake-form';
import { PageHeader } from '@/features/service-request/components/page-header';
import { getServiceMeta } from '@/features/dashboard/constants';
import { usePanConstraint } from '@/features/dashboard/pan-view-context';
import { PALETTE_CREAM } from '@/features/service-request/tokens';

export const Route = createFileRoute('/_protected/service-request/new/$serviceType')({
  component: NewServiceRequestPage,
});

function NewServiceRequestPage() {
  const { serviceType } = Route.useParams();
  const navigate = useNavigate();

  // URL param is kebab-case; API needs snake_case
  const serviceTypeSnake = kebabToSnake(serviceType);
  const serviceMeta = getServiceMeta(serviceTypeSnake);

  // Unfiltered, so the service resolves whichever PAN is currently active.
  const { data: catalog, isLoading: catalogLoading } = useGroupedServices();
  const matchedService = React.useMemo(
    () => catalog?.groups.flatMap((g) => g.services).find((s) => s.service_type === serviceTypeSnake),
    [catalog, serviceTypeSnake],
  );
  const panTypes = matchedService?.pan_types ?? [];
  const intakeQuestions = (matchedService?.stage_questions ?? []).filter(
    (q) => q.stage === 'service_selected',
  );

  // Constrains the nav bar's PAN switcher to the PAN types this service serves.
  usePanConstraint({
    kind: 'service',
    serviceLabel: serviceMeta.title,
    panTypes,
    loading: catalogLoading,
  });

  function handleSuccess(id: string) {
    void navigate({
      to: '/service-request/$id',
      params: { id },
      replace: true,
    });
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: PALETTE_CREAM }}>
      <PageHeader serviceTitle={serviceMeta.title} />
      <IntakeForm
        serviceTypeSnake={serviceTypeSnake}
        panTypes={panTypes}
        onSuccess={handleSuccess}
        stageQuestions={intakeQuestions}
        catalogLoading={catalogLoading}
      />
    </div>
  );
}
